import { put, select, takeLatest } from 'redux-saga/effects';
import { WALLET } from '@blink/components/src/types';
import { AbstractWallet, LOCAL_STORAGE, LocalStorage } from '@blink/components/src/utils';
import { Message } from '@blink/components/src/constants/messages';
import { Errors } from '@blink/components/src/constants/errors';
import {
    METAMASK_ERRORS,
    NETWORK,
    NETWORK_CHAIN_TO_NETWORK,
    UNSUPPORTED_NETWORK_ID,
} from '@blink/components/src/constants/global';

import {
    addActiveWallet,
    addNetwork,
    addWallets,
    addWalletsSaga as addWalletsSagaAction,
    removeWallets,
    setWalletConnect,
    SetWalletConnectType,
} from '@/shared/store/wallets/actions';
import {
    ADD_WALLET_CONNECT,
    ADD_WALLETS_SAGA,
    CHANGE_NETWORK_SAGA,
    DISCONNECT_WALLET_SAGA,
    RECONNECT_IF_WALLET_AVAILABLE_SAGA,
    DISCONNECT_PREVIOUS_WALLET,
} from '@/shared/store/wallets/constants';
import { Store } from '@/shared/store';
import { hideModalAction } from '@/shared/store/modals/actions';
import {
    addNetworksErrorAction,
    addWalletsErrorAction,
    clearErrorsAction,
} from '@/shared/store/errors/actions';
import { ActiveWallet } from '@/shared/store/wallets';
import { cleanLocalStorageWalletConnect } from '@/shared/utils/helpers';
import { NETWORKS_ERROR, WALLETS_ERROR } from '@/shared/store/errors/constants';
import { getAgreementsAction, setUserBorrows } from '@/shared/store/agreements/actions';
import { Agreement, ROLE } from '@/shared/api/borrow';
import { Vault } from '@/shared/api/vault';
import { getWalletMap } from '@/shared/utils/getWalletMap';

function* reconnectIfWalletAvailableSaga(): any {
    try {
        yield put(clearErrorsAction({ errorType: WALLETS_ERROR }));

        let isWalletAvailable = false;
        let attempts = 0;
        const maxAttempts = 5;
        let walletInstance: AbstractWallet | undefined;
        let walletType: WALLET | null = null;

        while (!isWalletAvailable && attempts < maxAttempts) {
            attempts++;

            walletType = LocalStorage.getItem(LOCAL_STORAGE.WALLET) as WALLET;

            const walletMap = getWalletMap();

            walletInstance = walletMap.get(walletType);

            isWalletAvailable = !!walletType && !!walletInstance && walletInstance.isAvailable();

            if (!isWalletAvailable) {
                yield new Promise((resolve) => setTimeout(resolve, 1000));
            }
        }

        if (isWalletAvailable && walletInstance && walletType) {
            const isWalletConnected = yield walletInstance.isConnected();

            if (isWalletConnected) {
                yield put(addWalletsSagaAction(walletType));

                const connectedDapp: string =
                    LocalStorage.getItem(LOCAL_STORAGE.CONNECTED_DAPP) || '';
                if (connectedDapp.length > 0) {
                    yield put(setWalletConnect(JSON.parse(connectedDapp)));
                }
            }
        }
    } catch (e: any) {
        yield put(
            addWalletsErrorAction({
                message: Errors.ADD_WALLETS + e.message,
            }),
        );
    }
}

function* disconnectPreviousWalletsSaga(): any {
    try {
        const storedType: WALLET = yield select((state: Store) => state.wallets.active.type);

        const walletMap = getWalletMap();

        const storedWallet: AbstractWallet = walletMap.get(storedType) as AbstractWallet;

        if (storedWallet) {
            yield storedWallet.disconnect();
            yield put(removeWallets());
            yield put(setUserBorrows([]));
        }
    } catch (e: any) {
        console.warn(e);
    }
}

function* addWalletsSaga({ payload: type }: any): any {
    try {
        yield put(clearErrorsAction({ errorType: WALLETS_ERROR }));
        yield put(clearErrorsAction({ errorType: NETWORKS_ERROR }));

        const walletConnectAddress: string =
            LocalStorage.getItem(LOCAL_STORAGE.WALLET_CONNECT_ADDRESS) || '';
        const walletType: string = LocalStorage.getItem(LOCAL_STORAGE.WALLET) || '';

        if (
            walletConnectAddress.length > 0 &&
            walletType === WALLET.WALLET_CONNECT &&
            type === WALLET.WALLET_CONNECT
        ) {
            const walletInfo: ActiveWallet = {
                address: walletConnectAddress,
                type: WALLET.WALLET_CONNECT,
            };
            yield put(addActiveWallet(walletInfo));

            const walletMap = getWalletMap();

            const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;
            const walletChainId = yield wallet.getChainId();
            const network: NETWORK =
                NETWORK_CHAIN_TO_NETWORK.get(`${walletChainId}`) ||
                (UNSUPPORTED_NETWORK_ID as NETWORK);
            yield put(addNetwork(network));

            return;
        }

        const walletMap = getWalletMap();

        const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;

        const wallets = yield wallet.connect();
        const address = wallets[0];

        yield put(addWallets({ wallets, type }));
        yield put(addActiveWallet({ address, type }));
        LocalStorage.setItem(LOCAL_STORAGE.WALLET, type);

        const borrowerAgreements: Agreement[] = yield select(
            (state: Store) => state.agreements.borrowerAgreements,
        );

        if (borrowerAgreements.length) {
            borrowerAgreements.map((agr) => (agr.noWalletInfo = false));
        }

        const lenderAgreements: Agreement[] = yield select(
            (state: Store) => state.agreements.lenderAgreements,
        );

        if (lenderAgreements.length) {
            lenderAgreements.map((agr) => (agr.noWalletInfo = false));
        }

        const vaults: Vault[] = yield select((state: Store) => state.vaults.vaults);

        if (vaults.length) {
            vaults.map((vault) => (vault.noWalletInfo = false));
        }

        if (type === WALLET.WALLET_CONNECT) {
            LocalStorage.setItem(LOCAL_STORAGE.WALLET_CONNECT_ADDRESS, address);
        }

        const walletChainId = yield wallet.getChainId();
        const chainId = parseInt(walletChainId).toString();
        const network: NETWORK =
            NETWORK_CHAIN_TO_NETWORK.get(chainId) || (UNSUPPORTED_NETWORK_ID as NETWORK);

        LocalStorage.setItem(LOCAL_STORAGE.NETWORK, network);
        yield put(addNetwork(network));
    } catch (e: any) {
        yield put(addWalletsErrorAction({ message: Errors.ADD_WALLETS + e.message }));
        yield put(addNetworksErrorAction({ message: Errors.ADD_NETWORK + e.message }));
        console.warn(e);
    }
}

function* changeNetworkSaga({ payload: network }: any): any {
    const type: WALLET = yield select((state: Store) => state.wallets.active.type);

    const walletMap = getWalletMap();

    const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;

    try {
        yield put(clearErrorsAction({ errorType: NETWORKS_ERROR }));
        yield put(clearErrorsAction({ errorType: WALLETS_ERROR }));

        yield put(addNetwork(network));
        yield wallet.changeNetwork(network);
        yield put(hideModalAction());
    } catch (e: any) {
        if (e?.code !== METAMASK_ERRORS.USER_REJECTED) {
            yield put(addWalletsErrorAction({ message: Message.CHANGE_NETWORK_ERROR + e.message }));
            console.warn(e);
        }
    }
}

function* disconnectWalletSaga() {
    const type: WALLET = yield select((state: Store) => state.wallets.active.type);

    const walletMap = getWalletMap();

    const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;
    yield wallet.disconnect();
    yield put(removeWallets());
    yield put(setUserBorrows([]));
    yield put(
        getAgreementsAction({
            role: ROLE.LENDER,
            withoutCache: true,
        }),
    );
    yield put(
        getAgreementsAction({
            role: ROLE.BORROWER,
            withoutCache: true,
        }),
    );
    cleanLocalStorageWalletConnect();
}

function* addWalletConnectSaga(): any {
    const connectedDapp: SetWalletConnectType = yield select(
        (state: Store) => state.wallets.walletConnect?.connectedDapp,
    );
    LocalStorage.setItem(LOCAL_STORAGE.CONNECTED_DAPP, JSON.stringify(connectedDapp));
}

export function* walletWatcher() {
    yield takeLatest(DISCONNECT_PREVIOUS_WALLET, disconnectPreviousWalletsSaga);
    yield takeLatest(ADD_WALLETS_SAGA, addWalletsSaga);
    yield takeLatest(RECONNECT_IF_WALLET_AVAILABLE_SAGA, reconnectIfWalletAvailableSaga);
    yield takeLatest(DISCONNECT_WALLET_SAGA, disconnectWalletSaga);
    yield takeLatest(CHANGE_NETWORK_SAGA, changeNetworkSaga);
    yield takeLatest(ADD_WALLET_CONNECT, addWalletConnectSaga);
}
